import * as React from 'react';
import { useSelector } from 'react-redux';
import { EmptyListingBanner } from 'shared/components/EmptyListingBanner';
import { OfferCardSkeleton } from 'shared/components/OfferCardSkeleton';
import { EmptyStateBannerContainer } from 'shared/containers/EmptyStateBannerContainer';
import { NewbuildingOffersCarouselContainer } from 'shared/containers/NewbuildingOffersCarouselContainer';
import { useOfferProps } from 'shared/hooks/useOfferProps';
import { selectIsEmptyStateVisible, selectSource } from 'shared/selectors';
import { selectIsSerpFetching } from 'shared/selectors/selectIsSerpFetching';
import { selectOffersPerPage } from 'shared/selectors/experiments/selectOffersPerPage';
import { selectIsNewbuildingOffersWithBannerCarouselVisible } from 'shared/selectors/newbuildingOffersCarousel';
import { selectKp } from 'shared/selectors/results';
import { selectIsSubsidisedMortgageZeroResultsBannerAvailable } from 'shared/selectors/selectIsSubsidisedMortgageZeroResultsBannerAvailable';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { getOfferCardCreator, integrateAd } from './helpers';
import { TFullOfferProps } from './types';
import { useOffersCallbacks } from './useOffersCallbacks';
import { withItemErrorBoundaries } from './utils/withItemErrorBoundaries';
import { EmptyKpSearchContainer } from '../../common/components/PreInfiniteBanner/EmptyKpSearch/container';
import { IAppState } from '../../common/state';
import { OffersLayout } from '../../components/OffersLayout';
import { type IKpOffersByCategoryCounter } from '../../offer';
import { selectIsCountrysideTrapEnabled } from '../../selectors/selectIsCountrysideTrapEnabled';
import { SubsidisedMortgageZeroResultsBannerContainer } from '../../serp/containers/SubsidisedMortgageZeroResultsBannerContainer';
import { dealTypeFromJsonQuery, FDealType, isSuburban, offerTypeFromJsonQuery } from '../../utils/category';

const selectIsCountrysideBuildersBannerAvailable = (state: IAppState) => state.isCountrysideBuildersBannerAvailable;

const EMPTY_LISTING_BANNER_TELEMETRY_KEY = 'emptyListingBannerShown';

/* istanbul ignore next */
export const OffersContainer: React.FC = () => {
  const { telemetry, logger } = useApplicationContext();
  const offerProps = useOfferProps();
  const allCallbacks = useOffersCallbacks({ userIsAuthenticated: offerProps.user.isAuthenticated });

  const isCountrysideTrapEnabled = useSelector(selectIsCountrysideTrapEnabled);
  const isCountrysideBuildersBannerAvailable = useSelector(selectIsCountrysideBuildersBannerAvailable);
  const kp = useSelector(selectKp);
  const perPage = useSelector(selectOffersPerPage);
  const isSubsidisedMortgageZeroResultsBannerAvailable = useSelector(
    selectIsSubsidisedMortgageZeroResultsBannerAvailable,
  );
  const isNewbuildingOffersWithBannerCarouselVisible = useSelector(selectIsNewbuildingOffersWithBannerCarouselVisible);
  const isEmptyStateVisible = useSelector(selectIsEmptyStateVisible);
  const isFetching = useSelector(selectIsSerpFetching);
  const source = useSelector(selectSource);

  const emptyKpOffersCounter = (kp && kp.offers_by_category_counter) || {};

  const isSuburbanListing = isSuburban(offerTypeFromJsonQuery(offerProps.jsonQuery));
  const dealType = dealTypeFromJsonQuery(offerProps.jsonQuery);

  const showEmptyKpSearch =
    Object.keys(emptyKpOffersCounter).filter((key: keyof IKpOffersByCategoryCounter) =>
      Number(emptyKpOffersCounter[key]),
    ).length > 0;

  const showCountrysideTrap = isCountrysideTrapEnabled && isSuburbanListing && dealType !== FDealType.RentDaily;
  const showSuburbanListingBanner = isSuburbanListing && [FDealType.Sale, FDealType.RentLongterm].includes(dealType);

  const { hiddenOffers, hiddenSuggestions } = offerProps.hideOffer;

  const offerCardCreatorProps: TFullOfferProps = {
    ...offerProps,
    ...allCallbacks,
  };

  const cardsWithAd = withItemErrorBoundaries(
    integrateAd({
      suggestions: offerProps.suggestions,
      currentPageNumber: offerProps.currentPageNumber,
      perPage,
      offers: offerProps.offers,
      jsonQuery: offerProps.jsonQuery,
      isPrintEnabled: offerProps.isPrintEnabled,
      offerCardCreator: getOfferCardCreator({ props: offerCardCreatorProps, source }),
      locationId: offerProps.jsonQuery.region ? offerProps.jsonQuery.region.value[0] : undefined,
      suggestionFragment: { isSimilar: false },
      hiddenOffers,
      hiddenSuggestions,
      mlRankingGuid: offerProps.mlRankingGuid,
      mlRankingModelVersion: offerProps.mlRankingModelVersion,
      isAgent: offerProps.user.isAuthenticated && offerProps.user.isAgent,
      showCountrysideTrap,
      extendedOffersOffset: offerProps.aggregatedOffersCount - offerProps.extendedOffersCount,
      keyPrefix: 'regular',
      isCountrysideBuildersBannerAvailable,
      servicesBannerPositions: offerCardCreatorProps.servicesBannerPositions,
      showSuburbanListingBanner,
      suburbanBuildersProjects: offerCardCreatorProps.suburbanBuildersProjects,
      isNewbuildingOffersWithBannerCarouselVisible,
    }),
    logger,
  );

  /**
   * Блок пустой выдачи из ручки поиска приоритетнее остальных баннеров: ручка сама решает,
   * что показать на пустой выдаче, и её ответ не должен перебиваться баннерами хоста.
   */
  const emptyListingBanner = isEmptyStateVisible ? (
    <EmptyStateBannerContainer />
  ) : isSubsidisedMortgageZeroResultsBannerAvailable ? (
    <SubsidisedMortgageZeroResultsBannerContainer />
  ) : showEmptyKpSearch ? (
    <EmptyKpSearchContainer />
  ) : (
    <EmptyListingBanner onShow={() => telemetry.increment(EMPTY_LISTING_BANNER_TELEMETRY_KEY)} />
  );

  /**
   * Пока выдача перезапрашивается, вместо объявлений показываем скелетоны — оверлея
   * со спиннером на странице больше нет.
   */
  const offersSkeleton = isFetching && (
    <>
      <OfferCardSkeleton />
      <OfferCardSkeleton />
    </>
  );

  return (
    <OffersLayout
      isEmpty={offerProps.offers.length === 0}
      isEmptyStateVisible={isEmptyStateVisible}
      offersSkeleton={offersSkeleton}
      newbuildingOffersCarousel={
        isNewbuildingOffersWithBannerCarouselVisible && <NewbuildingOffersCarouselContainer withMargin />
      }
      cardsWithAd={cardsWithAd}
      emptyListingBanner={emptyListingBanner}
    />
  );
};
